import {RoomBaseApiService} from "@/core/service/room.baseapi.service";
import {ErrorWrapper, ResponseWrapper} from "@/core/service/util";

export class RoomDocService extends RoomBaseApiService {

    async getRoomDocInfo (roomGroupId) {
        try {
            const response = await this.request().get(`/api/v3/room/${this.getRoomId()}/doc?roomGroupId=${roomGroupId||''}`)
            return new ResponseWrapper(response, response.data.data)
        } catch (error) {
            throw new ErrorWrapper(error)
        }
    }

    async getRoomDocInfoList () {
        try {
            const response = await this.request().get(`/api/v3/room/${this.getRoomId()}/docs`)
            return new ResponseWrapper(response, response.data.data)
        } catch (error) {
            throw new ErrorWrapper(error)
        }
    }

    async getRoomDocFilePages ({docId}) {
        try {
            const response = await this.request().get(`/api/v3/room/${this.getRoomId()}/doc/files/${docId}/pages`)
            return new ResponseWrapper(response, response.data.data)
        } catch (error) {
            throw new ErrorWrapper(error)
        }
    }

    async sendDraw (drawData) {
        try {
            const response = await this.request().post(`/api/v3/room/${this.getRoomId()}/doc/sendDraw`, drawData)
            return new ResponseWrapper(response, response.data.data)
        } catch (error) {
            throw new ErrorWrapper(error)
        }
    }

    async changeRoomDocFilePage ({docId, docPageId, roomGroupId='default'}) {
        try {
            const response = await this.request().post(`/api/v3/room/${this.getRoomId()}/doc/changePage`, {docId, docPageId, roomGroupId})
            return new ResponseWrapper(response, response.data.data)
        } catch (error) {
            throw new ErrorWrapper(error)
        }
    }

    async getRoomDocDrawList ({docId, docPageId, roomGroupId}) {
        try {
            const response = await this.request().get(`/api/v3/room/${this.getRoomId()}/doc/drawList`, {
                params: {docId, docPageId, roomGroupId}
            })
            return new ResponseWrapper(response, response.data.data)
        } catch (error) {
            throw new ErrorWrapper(error)
        }
    }

    async requestPresignedUrlForRoomDocFile() {
        try {
            const response = await this.request().post(`/api/v3/room/${this.getRoomId()}/doc/presignedUrlForUpload`)
            return new ResponseWrapper(response, response.data.data)
        } catch (error) {
            throw new ErrorWrapper(error)
        }
    }

    async completedPresignedUrlRoomDocFile(data) {
        try {
            const response = await this.request()
                                       .post(`/api/v3/room/${this.getRoomId()}/doc/completedPresignedUrl`,{
                                           ...data
                                       })
            return new ResponseWrapper(response, response.data.data)
        } catch (error) {
            throw new ErrorWrapper(error)
        }
    }

    async uploadToPresignedUrl ({url, file, onUploadProgress}) {
        try {
            const response = await this.request({ auth: false }).put(url, file, {
                headers: {
                    'Content-Type': file.type,
                },
                withCredentials: false,
                onUploadProgress: e => {
                    const percent = e.loaded * 100 / file.size;
                    e.percent = percent;
                    onUploadProgress(e);
                }
            })
            return new ResponseWrapper(response, response)
        } catch (error) {
            throw new ErrorWrapper(error)
        }
    }

    async createUploadPresignedUrl(data) {
        try {
            data.baseServerUrl = process.env.VUE_APP_API_URL;
            const response = await this.request().post(`/api/v3/room/${this.getRoomId()}/doc/createUploadPresignedUrl`, { ...data })
            return new ResponseWrapper(response, response.data.data)
        } catch (error) {
            throw new ErrorWrapper(error)
        }
    }
    async addFromPresignedUrl(data) {
        try {
            const response = await this.request()
                .post(`/api/v3/room/${this.getRoomId()}/doc/addFromPresignedUrl`,{
                    ...data
                })
            return new ResponseWrapper(response, response.data.data)
        } catch (error) {
            throw new ErrorWrapper(error)
        }
    }

    async uploadRoomDoc ({roomGroupId='default', filename, file, setRoomDoc, onUploadProgress}) {
        try {
            const data = new FormData();
            data.append('roomGroupId', roomGroupId);
            data.append('filename', filename);
            data.append('file', file);
            if(setRoomDoc) {
                data.append('setRoomDoc', setRoomDoc)
            }

            const response = await this.request().post(`/api/v3/room/${this.getRoomId()}/doc/files/add`, data, {
                onUploadProgress
            })
            return new ResponseWrapper(response, response.data.data)
        } catch (error) {
            throw new ErrorWrapper(error)
        }
    }

    async uploadRoomDocFileGroupList ({roomGroupIds, filename, file, setRoomDoc, onUploadProgress}) {
        try {
            const data = new FormData();
            data.append('roomGroupIds', roomGroupIds);
            data.append('filename', filename);
            data.append('file', file);
            if(setRoomDoc) {
                data.append('setRoomDoc', setRoomDoc)
            }

            const response = await this.request().post(`/api/v3/room/${this.getRoomId()}/doc/files/add/list`, data, {
                onUploadProgress
            })
            return new ResponseWrapper(response, response.data.data)
        } catch (error) {
            throw new ErrorWrapper(error)
        }
    }

    async createRoomDocDownloadUrl ({roomGroupId='default', docId, docPageId, thumbnail}) {
        try {
            const response = await this.request()
                .post(
                    `/api/v3/room/${this.getRoomId()}/doc/${docId}/createDownloadUrl?roomGroupId=${roomGroupId}`
                    + `${docPageId ? '&docPageId='+docPageId : ''}`
                    + `${thumbnail ? '&thumbnail=true' : ''}`
                )

            let sasUrl = response.data.data;
            let {url} = await this.createObjectURL({url: sasUrl});

            return new ResponseWrapper(response, {url})
        } catch (error) {
            throw new ErrorWrapper(error)
        }
    }

    async createObjectURL ({url}) {
        let res = await this.request({auth: false}).get(url, {
            responseType: 'blob',
            withCredentials: false
        });
        let blobUrl = URL.createObjectURL(new Blob([res.data]));
        return {url: blobUrl};
    }

    async removeRoomDoc ({roomGroupId, docId}) {
        const response = await this.request()
            .delete(`/api/v3/room/${this.getRoomId()}/doc/${docId}?roomGroupId=${roomGroupId}`);
        return new ResponseWrapper(response, response.data.data)
    }

    async changeRoomDocSharing (roomGroupId) {
        try {
            const response = await this.request().post(`/api/v3/room/${this.getRoomId()}/doc/changeSharing?roomGroupId=${roomGroupId || 'default'}`)
            return new ResponseWrapper(response, response.data.data)
        } catch (error) {
            throw new ErrorWrapper(error)
        }
    }
}

export let roomDocService = new RoomDocService()